import { useState, useCallback, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { AuthModal } from "@/components/ui/auth-modal";
import { useGenerate } from "@/hooks/use-generation";
import { useUserStore } from "@/stores/user-store";
import { T2I_MODEL, IMAGE_EDIT_MODEL } from "@/config/models";
import { UploadZone } from "@/components/generate/upload-zone";
import { Loader2, Play, Sparkles, Hexagon } from "lucide-react";
import { NsfwOrError } from "@/components/ui/nsfw-error";
import { UpsellModal } from "@/components/ui/upsell-modal";
import { cn } from "@/lib/utils";
import { trackViewContent } from "@/services/analytics";

const TABS = [
  { href: "/create-character", label: "Create" },
  { href: "/animate-character", label: "Animate" },
  { href: "/extend-video", label: "Extend" },
];

export function TextToImagePage() {
  const navigate = useNavigate();
  const { user, isSignedIn } = useUser();
  const { credits, isLoading: isUserLoading } = useUserStore();
  const generateMutation = useGenerate();
  const [searchParams] = useSearchParams();
  const [prompt, setPrompt] = useState(searchParams.get("prompt") || "");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [showAuth, setShowAuth] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);

  const isEditMode = !!imageUrl;
  const activeModel = isEditMode ? IMAGE_EDIT_MODEL : T2I_MODEL;
  const canGenerate = prompt.trim().length > 0;

  useEffect(() => {
    trackViewContent("Create Character", user?.id, { funnel_stage: "create" });
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || isUserLoading) return;

    if (credits < activeModel.creditCost) {
      setShowCreditsModal(true);
      return;
    }

    try {
      const params: Record<string, unknown> = {
        model_id: activeModel.id,
        prompt: prompt.trim(),
      };

      if (isEditMode) {
        params.input_url = imageUrl;
      }

      const result = await generateMutation.mutateAsync(params as any);
      if (result.video?.id) navigate(`/generation/${result.video.id}`);
    } catch (err) { console.error("Generation failed:", err); }
  }, [prompt, imageUrl, isEditMode, activeModel, credits, generateMutation, navigate, isUserLoading]);

  return (
    <div className="max-w-xl mx-auto px-5 md:px-8 pt-6 pb-16">
      <div className="flex items-center gap-1 mb-6 border-b border-white/6 pb-3">
        {TABS.map((tab) => (
          <Link key={tab.href} to={tab.href}
            className={cn(
              "px-3.5 py-1.5 rounded-lg text-[13px] font-semibold transition-all",
              tab.href === "/create-character"
                ? "bg-white/10 text-[#d4a574]"
                : "text-muted-foreground hover:text-foreground hover:bg-white/5",
            )}>
            {tab.label}
          </Link>
        ))}
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground tracking-tight">
          Create Character
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5">
          {isEditMode
            ? "Describe what to change about your character."
            : "Describe your character and AI will generate them. Upload an image to edit an existing one."}
        </p>
        <Link
          to="/animate-character"
          className="inline-flex items-center gap-1.5 mt-3 px-3.5 py-1.5 rounded-lg bg-[#d4a574]/10 border border-[#d4a574]/30 text-[13px] font-semibold text-[#d4a574] hover:bg-[#d4a574]/15 transition-colors"
        >
          <Play className="size-3.5" />
          Want to make an anime video? Try Animate
        </Link>
      </div>

      <div className="space-y-4">
        <UploadZone imageUrl={imageUrl} onImageChange={setImageUrl} />

        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
          placeholder={isEditMode
            ? "Change her outfit to a gothic lolita dress with black lace, add cat ears, make her eyes red..."
            : "A confident anime swordsman with silver hair tied back, sharp amber eyes, wearing a dark blue haori over white clothes, katana at his side, dramatic lighting..."
          }
          rows={isEditMode ? 3 : 5} className="input-field resize-none" />

        {!prompt.trim() && <div className="flex flex-wrap gap-1.5">
          {(isEditMode
            ? ["School uniform", "Battle armor", "Add cat ears", "Glowing eyes", "Power-up aura", "Add weapon", "Sailor outfit", "Dark transformation"]
            : ["Isekai hero", "Magical girl", "Mecha pilot", "Slice of life", "Shonen protagonist", "Villain arc", "School uniform", "Demon slayer", "Chibi"]
          ).map((tag) => (
            <button key={tag} type="button" onClick={() => setPrompt((p) => p ? `${p}, ${tag.toLowerCase()}` : tag.toLowerCase())}
              className="text-[12px] font-semibold text-muted-foreground bg-white/5 border border-white/8 px-3 py-1.5 rounded-lg hover:bg-white/10 hover:text-foreground transition-colors">
              {tag}
            </button>
          ))}
        </div>}

        <Show when="signed-out">
          <button onClick={() => setShowAuth(true)} className="btn-primary w-full py-3 text-[14px] flex items-center justify-center gap-2 rounded-xl">
            <Sparkles className="size-4" />
            Generate Character Image
            <span className="inline-flex items-center gap-1 ml-1 opacity-70 text-[12px]">
              · <Hexagon className="size-3" />{activeModel.creditCost}
            </span>
          </button>
          <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
        </Show>
        <Show when="signed-in">
          {isUserLoading ? (
            <div className="w-full h-[46px] rounded-xl bg-[#d4a574]/20 animate-pulse" />
          ) : (
            <button onClick={handleGenerate} disabled={!canGenerate || generateMutation.isPending} className="btn-primary w-full py-3 text-[14px] flex items-center justify-center gap-2 rounded-xl">
              {generateMutation.isPending ? (
                <><Loader2 className="size-4 animate-spin" />Generating...</>
              ) : (
                <>
                  <Sparkles className="size-4" />
                  Generate Character Image
                  <span className="inline-flex items-center gap-1 ml-1 opacity-70 text-[12px]">
                    · <Hexagon className="size-3" />{activeModel.creditCost}
                  </span>
                </>
              )}
            </button>
          )}
        </Show>

        {generateMutation.isError && (
          <NsfwOrError message={generateMutation.error?.message} />
        )}
      </div>

      <p className="text-[11px] text-muted-foreground mt-8 text-center">
        Then <Link to="/animate-character" className="text-amber-700 font-semibold hover:underline">animate</Link> your character.
      </p>

      <UpsellModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        creditCost={activeModel.creditCost}
        credits={credits}
      />
    </div>
  );
}
